/**
 * Whether there is anywhere to go forward to.
 *
 * WHY THIS HAS TO BE COUNTED BY HAND. The browser history knows perfectly well
 * whether a forward entry exists, and tells nobody: there is no
 * `history.canGoForward`, and Angular's Location does not add one. Calling
 * forward() when there is nothing ahead simply does nothing, which is the
 * worst possible answer for a gesture: the finger moves, the screen does not,
 * and the person cannot tell whether the application is broken or the gesture
 * was wrong.
 *
 * So the depth is tracked here instead, from the navigations that go past.
 *
 * WHAT MOVES IT
 *
 *   a step back        one more place to go forward to
 *   a step forward     one fewer
 *   a new page pushed  nothing ahead any more, the branch is abandoned
 *
 * That last rule is the one people forget, and it is the one that makes a
 * forward gesture safe: the moment somebody navigates somewhere new, whatever
 * they had gone back from stops existing, exactly as in a browser.
 */
export type NavigationStep = 'back' | 'forward' | 'push';

export class ForwardHistory {
  private depth = 0;

  /** How many pages lie ahead. Zero means a forward gesture must do nothing. */
  get available(): number {
    return this.depth;
  }

  get canGoForward(): boolean {
    return this.depth > 0;
  }

  record(step: NavigationStep): void {
    if (step === 'back') {
      this.depth += 1;
      return;
    }
    if (step === 'forward') {
      // Never below zero: a forward that was not preceded by a back is a
      // restored session or a gesture that raced, and pretending it left a
      // debt would block the next real forward.
      this.depth = Math.max(0, this.depth - 1);
      return;
    }
    this.depth = 0;
  }
}

/**
 * What a navigation was, read from what Angular hands over.
 *
 * A popstate carries the id of the entry being restored. Older than the one we
 * are on means a step back, newer means a step forward. Everything else is a
 * new page, whatever route it leads to.
 */
export function stepFromNavigation(input: {
  trigger: string;
  restoredNavigationId?: number | null;
  currentNavigationId?: number | null;
}): NavigationStep {
  if (input.trigger !== 'popstate') {
    return 'push';
  }
  const restored = input.restoredNavigationId;
  const current = input.currentNavigationId;
  if (typeof restored !== 'number' || typeof current !== 'number') {
    // A popstate we cannot place. Treated as a step back, which is what a
    // popstate almost always is, and which errs towards offering a forward
    // rather than towards a gesture that silently does nothing.
    return 'back';
  }
  return restored < current ? 'back' : 'forward';
}
